<script module>
    export { default as layout } from '../../layouts/AppLayout.svelte';
</script>

<script lang="ts">
    import { flip } from 'svelte/animate';
    import { Link, router } from '@inertiajs/svelte';
    import CaseCard from '../../components/CaseCard.svelte';
    import ConfirmModal from '../../components/ConfirmModal.svelte';
    import { newMethod, edit, destroy } from '@/routes/cases';
    import { formatDate } from '../../utils/dates';

    let deleteModalOpen = $state(false);
    let pendingDeleteId = $state<number | null>(null);

    function requestDelete(id: number, e: MouseEvent) {
        e.preventDefault();
        e.stopPropagation();
        pendingDeleteId = id;
        deleteModalOpen = true;
    }

    function shatterElement(el: HTMLElement): Promise<void> {
        const rect = el.getBoundingClientRect();
        const cols = 4;
        const rows = 4;
        const pieceW = rect.width / cols;
        const pieceH = rect.height / rows;

        el.style.visibility = 'hidden';

        const pieces: HTMLElement[] = [];
        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                const piece = document.createElement('div');
                piece.style.cssText = `
                    position: fixed;
                    left: ${rect.left + c * pieceW}px;
                    top: ${rect.top + r * pieceH}px;
                    width: ${pieceW}px;
                    height: ${pieceH}px;
                    overflow: hidden;
                    z-index: 100;
                    pointer-events: none;
                `;

                const inner = el.cloneNode(true) as HTMLElement;
                inner.style.cssText = `
                    position: absolute;
                    left: ${-c * pieceW}px;
                    top: ${-r * pieceH}px;
                    width: ${rect.width}px;
                    height: ${rect.height}px;
                    visibility: visible;
                `;
                piece.appendChild(inner);

                document.body.appendChild(piece);
                pieces.push(piece);
            }
        }

        return new Promise((resolve) => {
            requestAnimationFrame(() => {
                pieces.forEach((piece) => {
                    const tx = (Math.random() - 0.5) * 600;
                    const ty = (Math.random() - 0.5) * 400 - 200;
                    const rot = (Math.random() - 0.5) * 720;
                    piece.style.transition = 'transform 1000ms cubic-bezier(.2,.8,.3,1), opacity 1000ms ease-out';
                    piece.style.transform = `translate(${tx}px, ${ty}px) rotate(${rot}deg) scale(0.3)`;
                    piece.style.opacity = '0';
                });

                setTimeout(() => {
                    pieces.forEach((p) => p.remove());
                    resolve();
                }, 1050);
            });
        });
    }

    function confirmDelete() {
        if (pendingDeleteId === null) return;
        const id = pendingDeleteId;
        pendingDeleteId = null;

        const card = document.querySelector(`[data-draft-id="${id}"]`) as HTMLElement | null;
        if (card) {
            shatterElement(card).then(() => {
                router.delete(destroy.url(id), { preserveScroll: true });
            });
        } else {
            router.delete(destroy.url(id), { preserveScroll: true });
        }
    }

    interface CaseSummary {
        id: number;
        history_excerpt: string;
        institution?: string;
        status: string;
        computed_status: string;
        has_question: boolean;
        topic_name?: string;
        image_url: string | null;
        image_srcset?: { url: string; width: number }[];
        updated_at: string;
    }

    let { cases = [] }: { cases: CaseSummary[] } = $props();

    const drafts = $derived(cases.filter(c => c.computed_status === 'draft' || c.computed_status === 'needs_revision'));
    const submitted = $derived(cases.filter(c => c.computed_status !== 'draft' && c.computed_status !== 'needs_revision'));

    function userFacingStatus(status: string): string {
        switch (status) {
            case 'submitted': return 'submitted';
            case 'needs_revision': return 'needs_revision';
            case 'declined': return 'declined';
            default: return 'accepted';
        }
    }

    const statusColors: Record<string, string> = {
        draft: 'bg-gray-800 text-gray-400',
        submitted: 'bg-amber-900 text-amber-300',
        accepted: 'bg-emerald-900 text-emerald-300',
        needs_revision: 'bg-violet-900 text-violet-300',
        declined: 'bg-rose-900 text-rose-300',
    };

    const statusLabels: Record<string, string> = {
        draft: 'Draft',
        submitted: 'Submitted',
        accepted: 'Accepted',
        needs_revision: 'Needs Revision',
        declined: 'Declined',
    };

    const statusIcons: Record<string, { viewBox: string; path: string }> = {
        draft: { viewBox: '0 0 512 512', path: 'M36.4 353.8c4.1-14.6 11.8-27.9 22.6-38.7l294-294C366.4 7.6 384.8 0 404 0s37.6 7.6 51.1 21.2l35.7 35.7C504.4 70.4 512 88.8 512 108s-7.6 37.6-21.2 51.1l-294 294c-10.7 10.7-24.1 18.5-38.7 22.6L30.4 511.1c-8.3 2.3-17.3 0-23.4-6.2s-8.5-15.1-6.2-23.4L36.4 353.8zM386.8 55.1l-52.9 52.9 70.1 70.1 52.9-52.9c4.6-4.6 7.1-10.7 7.1-17.2s-2.6-12.6-7.1-17.2L421.2 55.1C416.6 50.6 410.4 48 404 48s-12.6 2.6-17.2 7.1zM300 141.9L92.9 349.1c-4.9 4.9-8.4 10.9-10.3 17.6l-24.1 86.9 86.9-24.1c6.6-1.8 12.7-5.4 17.6-10.3L370.1 212 300 141.9z' },
        submitted: { viewBox: '0 0 576 512', path: 'M290.5 287.7L491.4 86.9 359 456.3 290.5 287.7zM457.4 53L256.6 253.8 88 185.3 457.4 53zM38.1 216.8l205.8 83.6 83.6 205.8c5.3 13.1 18.1 21.7 32.3 21.7 14.7 0 27.8-9.2 32.8-23.1L570.6 8c3.5-9.8 1-20.6-6.3-28s-18.2-9.8-28-6.3L39.4 151.7c-13.9 5-23.1 18.1-23.1 32.8 0 14.2 8.6 27 21.7 32.3z' },
        accepted: { viewBox: '0 0 512 512', path: 'M256 512a256 256 0 1 1 0-512 256 256 0 1 1 0 512zm0-464a208 208 0 1 0 0 416 208 208 0 1 0 0-416zm70.7 121.9c7.8-10.7 22.8-13.1 33.5-5.3 10.7 7.8 13.1 22.8 5.3 33.5L243.4 366.1c-4.1 5.7-10.5 9.3-17.5 9.8-7 .5-13.9-2-18.8-6.9l-55.9-55.9c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l36 36 105.6-145.2z' },
        needs_revision: { viewBox: '0 0 512 512', path: 'M39.8 393.1C49.7 408.7 61.4 423.5 75 437 175 537 337 537 437 437S537 175 437 75C342.8-19.3 193.3-24.7 92.7 58.8l45.5 45.5c75.3-58.6 184.3-53.3 253.5 15.9 75 75 75 196.5 0 271.5s-196.5 75-271.5 0c-10.2-10.2-19-21.3-26.4-33-9.5-14.9-29.3-19.3-44.2-9.8s-19.3 29.3-9.8 44.2zM168 192L24 192c-13.3 0-24-10.7-24-24L0 24C0 14.3 5.8 5.5 14.8 1.8S34.1 .2 41 7L185 151c6.9 6.9 8.9 17.2 5.2 26.2S177.7 192 168 192z' },
        declined: { viewBox: '0 0 512 512', path: 'M99.5 144.8c12.5-17.5 27.8-32.8 45.3-45.3L412.5 367.2c-12.5 17.5-27.8 32.8-45.3 45.3L99.5 144.8zM256 64a192 192 0 1 1 0 384 192 192 0 1 1 0-384zm0 448a256 256 0 1 0 0-512 256 256 0 1 0 0 512z' },
    };
</script>

<svelte:head>
    <title>My Cases — Case of the Day</title>
</svelte:head>

{#snippet plusIcon()}
    <svg class="size-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M248 56c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 176-176 0c-13.3 0-24 10.7-24 24s10.7 24 24 24l176 0 0 176c0 13.3 10.7 24 24 24s24-10.7 24-24l0-176 176 0c13.3 0 24-10.7 24-24s-10.7-24-24-24l-176 0 0-176z"/></svg>
{/snippet}

{#snippet statusPill(status: string)}
    <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs {statusColors[status] ?? 'bg-gray-800 text-gray-300'}">
        {#if statusIcons[status]}
            <svg class="size-3" viewBox={statusIcons[status].viewBox} fill="currentColor"><path d={statusIcons[status].path}/></svg>
        {/if}
        {statusLabels[status] ?? status}
    </span>
{/snippet}

<h1 class="text-2xl font-bold text-gray-100 mb-6">My Cases</h1>

    <!-- Drafts -->
    <section class="mb-10">
        <h2 class="text-lg font-semibold text-gray-200 mb-4">Drafts</h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link href={newMethod()} class="group bg-gray-900 rounded-lg shadow border-2 border-dashed border-gray-700 hover:border-cyan-600 transition-colors flex items-center justify-center min-h-40">
                <div class="flex flex-col items-center gap-2 text-gray-500 group-hover:text-cyan-400 transition-colors">
                    {@render plusIcon()}
                    <span class="text-sm font-medium">New Case</span>
                </div>
            </Link>
            {#each drafts as c (c.id)}
                    <div animate:flip={{ duration: 500 }} class="flex flex-col">
                    <Link href={edit(c.id)} data-draft-id={c.id} class="group bg-gray-900 rounded-lg shadow border border-gray-700 {c.computed_status === 'needs_revision' ? 'hover:border-violet-600' : 'hover:border-gray-600'} hover:shadow-md transition-all overflow-hidden flex flex-col flex-1">
                        <CaseCard
                            imageUrl={c.image_url}
                            imageSrcset={c.image_srcset}
                            topicName={c.topic_name}
                            historyExcerpt={c.history_excerpt}
                            institution={c.institution}
                            hoverZoom
                        >
                            {#snippet meta()}
                                <div class="flex items-center justify-between">
                                    {@render statusPill(c.computed_status === 'needs_revision' ? 'needs_revision' : 'draft')}
                                    <div class="flex items-center gap-2">
                                        <span class="text-xs text-gray-400">{formatDate(c.updated_at)}</span>
                                        {#if c.computed_status === 'draft'}
                                            <button
                                                type="button"
                                                onclick={(e) => requestDelete(c.id, e)}
                                                class="size-6 flex items-center justify-center rounded-full border border-rose-700/50 bg-rose-950/50 text-rose-400 hover:bg-rose-900 hover:border-rose-600 transition-colors"
                                                aria-label="Delete draft"
                                            >
                                                <svg class="size-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" fill="currentColor"><path d="M170.5 51.6L151.5 80l145 0-19-28.4c-1.5-2.2-4-3.6-6.7-3.6l-93.7 0c-2.7 0-5.2 1.3-6.7 3.6zm147-26.6L354.2 80 368 80l48 0 8 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-8 0 0 304c0 44.2-35.8 80-80 80l-224 0c-44.2 0-80-35.8-80-80l0-304-8 0c-13.3 0-24-10.7-24-24S10.7 80 24 80l8 0 48 0 13.8 0 36.7-55.1C140.9 9.4 158.4 0 177.1 0l93.7 0c18.7 0 36.2 9.4 46.6 24.9zM80 128l0 304c0 17.7 14.3 32 32 32l224 0c17.7 0 32-14.3 32-32l0-304L80 128zm80 64l0 208c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-208c0-8.8 7.2-16 16-16s16 7.2 16 16zm80 0l0 208c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-208c0-8.8 7.2-16 16-16s16 7.2 16 16zm80 0l0 208c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-208c0-8.8 7.2-16 16-16s16 7.2 16 16z"/></svg>
                                            </button>
                                        {/if}
                                    </div>
                                </div>
                            {/snippet}
                        </CaseCard>
                    </Link>
                    </div>
                {/each}
        </div>
    </section>

    <!-- Submitted -->
    {#if submitted.length > 0}
        <section>
            <h2 class="text-lg font-semibold text-gray-200 mb-4">Submitted</h2>
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {#each submitted as c (c.id)}
                    <div class="bg-gray-900 rounded-lg shadow border border-gray-700 overflow-hidden flex flex-col">
                        <CaseCard
                            imageUrl={c.image_url}
                            imageSrcset={c.image_srcset}
                            topicName={c.topic_name}
                            historyExcerpt={c.history_excerpt}
                            institution={c.institution}
                        >
                            {#snippet meta()}
                                <div class="flex items-center justify-between">
                                    {@render statusPill(userFacingStatus(c.computed_status))}
                                    <span class="text-xs text-gray-400">{formatDate(c.updated_at)}</span>
                                </div>
                            {/snippet}
                        </CaseCard>
                    </div>
                {/each}
            </div>
        </section>
    {/if}

<ConfirmModal
    bind:open={deleteModalOpen}
    title="Delete this draft?"
    message="This draft and all its content will be permanently deleted. This cannot be undone."
    confirmLabel="Delete"
    variant="danger"
    panelClass="danger-glow"
    onconfirm={confirmDelete}
    oncancel={() => { pendingDeleteId = null; }}
/>

